/**
 * Phone Authentication Modal Component
 */

import { useState } from '@wordpress/element';
import { useAuth } from '../context/AuthContext';

export default function PhoneAuthModal({ onClose, onSuccess }) {
    const { requestCode, verifyCode, error } = useAuth();
    const [step, setStep] = useState('phone'); // 'phone' or 'code'
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState('');

    const t = window.rialtoData?.translations || {};

    const formatPhoneNumber = (value) => {
        // Remove non-digits
        const digits = value.replace(/\D/g, '');

        // Format as 02x xxx xxxx
        if (digits.length <= 3) {
            return digits;
        } else if (digits.length <= 6) {
            return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        } else {
            return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
        }
    };

    const handlePhoneChange = (e) => {
        setPhone(formatPhoneNumber(e.target.value));
    };

    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setLoading(true);

        try {
            await requestCode(phone.replace(/\s/g, ''), fullName.trim(), email.trim());
            setStep('code');
        } catch (err) {
            setLocalError(err.message || 'Failed to send code');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setLoading(true);

        try {
            const result = await verifyCode(phone.replace(/\s/g, ''), code, rememberMe);
            if (result.success) {
                onSuccess();
            }
        } catch (err) {
            setLocalError(err.message || 'Invalid code');
        } finally {
            setLoading(false);
        }
    };

    const isPhoneValid = phone.replace(/\s/g, '').length >= 9;
    const isNameValid = fullName.trim().length >= 2;

    return (
        <div className="rialto-modal-overlay active" onClick={onClose}>
            <div className="rialto-modal" onClick={(e) => e.stopPropagation()}>
                <button className="rialto-modal-close" onClick={onClose}>
                    &times;
                </button>

                {step === 'phone' ? (
                    <>
                        <h2 className="rialto-modal-title">
                            {t.registerForReminders || 'Register for SMS Reminders'}
                        </h2>
                        <p className="rialto-modal-desc">
                            {t.registerDesc || 'Sign up with your details to receive screening reminders via SMS.'}
                        </p>

                        <form onSubmit={handlePhoneSubmit}>
                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="fullName">
                                    {t.fullName || 'Full Name'}
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    className="rialto-form-input"
                                    placeholder="John Smith"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    required
                                    autoFocus
                                    autoComplete="name"
                                />
                            </div>

                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="email">
                                    {t.emailLabel || 'Email'} <span className="rialto-form-optional">({t.optional || 'optional'})</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className="rialto-form-input"
                                    placeholder="john@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                />
                            </div>

                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="phone">
                                    {t.mobileNumber || 'NZ Mobile Number'}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    className="rialto-form-input"
                                    placeholder="02x xxx xxxx"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    required
                                    autoComplete="tel"
                                />
                            </div>

                            {(localError || error) && (
                                <div className="rialto-form-error">
                                    {localError || error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="rialto-btn rialto-btn-primary"
                                disabled={loading || !isPhoneValid || !isNameValid}
                                style={{ width: '100%' }}
                            >
                                {loading ? 'Sending...' : (t.sendCode || 'Send Code')}
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <h2 className="rialto-modal-title">
                            {t.enterCode || 'Enter Code'}
                        </h2>
                        <p className="rialto-modal-desc">
                            We've sent a 6-digit code to {phone}
                        </p>

                        <form onSubmit={handleCodeSubmit}>
                            <div className="rialto-form-group">
                                <label className="rialto-form-label" htmlFor="code">
                                    Verification Code
                                </label>
                                <input
                                    type="text"
                                    id="code"
                                    className="rialto-form-input"
                                    placeholder="123456"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    required
                                    autoFocus
                                    maxLength={6}
                                    style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }}
                                />
                            </div>

                            <div className="rialto-form-group">
                                <label className="rialto-form-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    {t.rememberMe || 'Remember me for 30 days'}
                                </label>
                            </div>

                            {(localError || error) && (
                                <div className="rialto-form-error">
                                    {localError || error}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="rialto-btn rialto-btn-primary"
                                disabled={loading || code.length !== 6}
                                style={{ width: '100%' }}
                            >
                                {loading ? 'Verifying...' : (t.verify || 'Verify')}
                            </button>

                            <button
                                type="button"
                                className="rialto-btn rialto-btn-secondary"
                                onClick={() => setStep('phone')}
                                style={{ width: '100%', marginTop: '10px' }}
                            >
                                Use different number
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
